import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../../auth/auth.action";
import { Jurisdiction } from "../../jurisdiction/jurisdiction.model";
import { JurisdictionSelector } from "../../jurisdiction/jurisdiction.selector";
import { MessageScopeType, MessageType } from "../../message/message.model";
import { MessageService } from "../../message/message.service";
import { SnackBarCacheService } from "../../snackbar/snackbar-cache.service";
import { CoreState } from "../../state";

import { DEPOSIT_LIMIT_ACTION_TYPE, DepositLimitAction } from "./deposit-limit.action";
import { DepositLimitConfigService } from "./deposit-limit.config";
import { DEPOSIT_LIMIT_MESSAGE_CONTEXT, HGC_DEPOSIT_LIMIT_80_ID } from "./deposit-limit.const";
import {
	DepositLimit,
	DepositLimitMessageCodes,
	DepositLimitParams,
	DepositLimitType,
	DepositLimitUnconfirmedParams,
} from "./deposit-limit.model";
import { DepositLimitService } from "./deposit-limit.service";

/**
 * @deprecated [fabric-deprecated] - Use DepositLimitService from app-responsible-gaming instead
 */
@Injectable()
export class DepositLimitEffect {
	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetAllDepositLimits instead
	 */
	depositLimits$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DEPOSIT_LIMIT_ACTION_TYPE.getAll),
			switchMap(() =>
				this.service.getAll().pipe(
					map((response) => this.action.getAllSuccess(response)),
					catchError((error: AppError) => of(this.action.getAllFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateDepositLimit instead
	 */
	depositLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DEPOSIT_LIMIT_ACTION_TYPE.update),
			map((x: ActionWithPayload<DepositLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.update(params).pipe(
					map((response) => this.action.updateSuccess(response)),
					catchError((error: AppError) => of(this.action.updateFail(error)))
				)
			)
		)
	);

	depositLimitMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DEPOSIT_LIMIT_ACTION_TYPE.updateSuccess),
				map((x: ActionWithPayload<DepositLimit>) => x.payload),
				concatLatestFrom(() => this.store.select(JurisdictionSelector.get)),
				filter(([, jurisdiction]) => jurisdiction === Jurisdiction.Hgc),
				tap(([depositLimit]) => {
					if (!depositLimit.queuedDepositLimit) {
						this.snackBarCacheService.removeSnackbarDoNotShowAgain(HGC_DEPOSIT_LIMIT_80_ID);
						return;
					}
					const now = Date.now();
					const startDate = new Date(depositLimit.queuedDepositLimit.startDate).getTime();
					this.snackBarCacheService.changeSnackbarDoNotShowAgainExpiration(
						HGC_DEPOSIT_LIMIT_80_ID,
						startDate - now
					);
				})
			),
		{ dispatch: false }
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.RemoveDepositLimit instead
	 */
	depositLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DEPOSIT_LIMIT_ACTION_TYPE.remove),
			map((x: ActionWithPayload<DepositLimitType>) => x.payload),
			switchMap((params) =>
				this.service.remove(params).pipe(
					map((response) => this.action.deleteSuccess(response)),
					catchError((error: AppError) => of(this.action.deleteFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetUnconfirmedDepositLimits instead
	 */
	getUnconfirmed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DEPOSIT_LIMIT_ACTION_TYPE.getUnconfirmed),
			switchMap(() =>
				this.service.getUnconfirmed().pipe(
					map((response) => this.action.getUnconfirmedSuccess(response)),
					catchError((error: AppError) => of(this.action.getUnconfirmedFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.AcceptUnconfirmedDepositLimit instead
	 */
	acceptUnconfirmed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DEPOSIT_LIMIT_ACTION_TYPE.acceptUnconfirmed),
			map((x: ActionWithPayload<DepositLimitUnconfirmedParams>) => x.payload),
			switchMap((params) =>
				this.service.acceptUnconfirmed(params).pipe(
					map(() => this.action.acceptUnconfirmedSuccess()),
					catchError((error: AppError) => of(this.action.acceptUnconfirmedFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.DeclineUnconfirmedDepositLimit instead
	 */
	declineUnconfirmed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DEPOSIT_LIMIT_ACTION_TYPE.declineUnconfirmed),
			map((x: ActionWithPayload<DepositLimitUnconfirmedParams>) => x.payload),
			switchMap((params) =>
				this.service.declineUnconfirmed(params).pipe(
					map(() => this.action.declineUnconfirmedSuccess()),
					catchError((error: AppError) => of(this.action.declineUnconfirmedFail(error)))
				)
			)
		)
	);

	getAll$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				AUTH_ACTION_TYPE.sessionStart,
				DEPOSIT_LIMIT_ACTION_TYPE.acceptUnconfirmedSuccess,
				DEPOSIT_LIMIT_ACTION_TYPE.declineUnconfirmedSuccess
			),
			filter(() => this.configService.getIsReconfirmationRequired()),
			map(() => this.action.getAll())
		)
	);

	getUnconfirmedOnSessionStart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.configService.getIsReconfirmationRequired()),
			map(() => this.action.getUnconfirmed())
		)
	);

	clearOnSessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.clear())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[DEPOSIT_LIMIT_ACTION_TYPE.getAllFail]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.getAll,
				[DEPOSIT_LIMIT_ACTION_TYPE.updateFail]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
				[DEPOSIT_LIMIT_ACTION_TYPE.removeFail]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.delete,
				[DEPOSIT_LIMIT_ACTION_TYPE.acceptUnconfirmedFail]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.acceptUnconfirmed,
				[DEPOSIT_LIMIT_ACTION_TYPE.declineUnconfirmedFail]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.declineUnconfirmed,
			}),
			this.messageService.mapClearByContext$({
				[DEPOSIT_LIMIT_ACTION_TYPE.getAll]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.getAll,
				[DEPOSIT_LIMIT_ACTION_TYPE.update]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
				[DEPOSIT_LIMIT_ACTION_TYPE.remove]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.delete,
				[DEPOSIT_LIMIT_ACTION_TYPE.acceptUnconfirmed]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.acceptUnconfirmed,
				[DEPOSIT_LIMIT_ACTION_TYPE.declineUnconfirmed]: DEPOSIT_LIMIT_MESSAGE_CONTEXT.declineUnconfirmed,
			}),
			this.messageService.map$({
				[DEPOSIT_LIMIT_ACTION_TYPE.updateSuccess]: {
					context: DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: DepositLimitMessageCodes.UpdateSuccess,
				},
				[DEPOSIT_LIMIT_ACTION_TYPE.removeSuccess]: {
					context: DEPOSIT_LIMIT_MESSAGE_CONTEXT.delete,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: DepositLimitMessageCodes.DeleteSuccess,
				},
				[DEPOSIT_LIMIT_ACTION_TYPE.acceptUnconfirmedSuccess]: {
					context: DEPOSIT_LIMIT_MESSAGE_CONTEXT.acceptUnconfirmed,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: DepositLimitMessageCodes.AcceptUnconfirmedSuccess,
				},
				[DEPOSIT_LIMIT_ACTION_TYPE.declineUnconfirmedSuccess]: {
					context: DEPOSIT_LIMIT_MESSAGE_CONTEXT.declineUnconfirmed,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: DepositLimitMessageCodes.DeclineUnconfirmedSuccess,
				},
			})
		)
	);

	constructor(
		private store: Store<CoreState>,
		private actions$: Actions,
		private action: DepositLimitAction,
		private service: DepositLimitService,
		private configService: DepositLimitConfigService,
		private messageService: MessageService,
		private snackBarCacheService: SnackBarCacheService
	) {}
}
